import {
    Component, ChangeDetectionStrategy, inject, signal, computed,
    input, output, OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LogService } from '../../../../core/services/log.service';
import { TwoFactorAuthService } from '../../../../core/services/two-factor-auth.service';

import { PrimeTemplate } from '@openng/optimus-ui/api';
import { Dialog } from '@openng/optimus-ui/dialog';
import { Button } from '@openng/optimus-ui/button';
import { ProgressSpinner } from '@openng/optimus-ui/progressspinner';
import { Message } from '@openng/optimus-ui/message';
import { InputText } from '@openng/optimus-ui/inputtext';

type RegistrationStep = 'check' | 'ready' | 'registering' | 'success' | 'error';

@Component({
    selector: 'webauthn-register-modal',
    templateUrl: './webauthn-register-modal.component.html',
    styleUrls: ['./webauthn-register-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, Dialog, Button, ProgressSpinner, Message, TranslatePipe, PrimeTemplate, InputText]
})
export class WebAuthnRegisterModalComponent implements OnInit {
    private readonly log = inject(LogService);
    private readonly twoFactorService = inject(TwoFactorAuthService);
    private readonly translate = inject(TranslateService);

    // Inputs
    visible = input<boolean>(false);
    token = input<string>('');

    // Outputs
    visibleChange = output<boolean>();
    registered = output<void>();

    // State
    currentStep = signal<RegistrationStep>('check');
    isLoading = signal(false);
    errorMessage = signal<string | null>(null);
    webAuthnSupported = signal(false);
    platformAuthAvailable = signal(false);
    serverEnabled = signal(false);
    credentialName = signal('');
    deviceName = signal('');

    // Computed
    canRegister = computed(() => {
        return this.webAuthnSupported() && this.serverEnabled() && this.currentStep() === 'ready';
    });

    stepMessage = computed(() => {
        switch (this.currentStep()) {
            case 'check':
                return this.translate.instant('auth.webauthn_checking');
            case 'ready':
                return this.translate.instant('auth.webauthn_ready');
            case 'registering':
                return this.translate.instant('auth.webauthn_registering');
            case 'success':
                return this.translate.instant('auth.webauthn_success_title');
            case 'error':
                return this.errorMessage() || 'Error';
        }
    });

    ngOnInit(): void {
        this.checkCompatibility();
    }

    onShow(): void {
        this.currentStep.set('check');
        this.errorMessage.set(null);
        this.deviceName.set('');
        this.checkCompatibility();
    }

    private async checkCompatibility(): Promise<void> {
        this.isLoading.set(true);

        try {
            // Check browser support
            const browserSupported = this.twoFactorService.isWebAuthnSupported();
            this.webAuthnSupported.set(browserSupported);

            if (!browserSupported) {
                this.currentStep.set('error');
                this.errorMessage.set('WebAuthn is not supported in this browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge.');
                this.isLoading.set(false);
                return;
            }

            // Check platform authenticator availability
            const platformAvailable = await this.twoFactorService.isPlatformAuthenticatorAvailable();
            this.platformAuthAvailable.set(platformAvailable);

            // Check server status
            this.twoFactorService.getWebAuthnStatus().subscribe({
                next: (enabled) => {
                    this.serverEnabled.set(enabled);
                    if (!enabled) {
                        this.currentStep.set('error');
                        this.errorMessage.set('WebAuthn is not enabled on this server. Please contact your administrator.');
                    } else {
                        this.currentStep.set('ready');
                    }
                    this.isLoading.set(false);
                },
                error: (err) => {
                    this.log.error('Error checking WebAuthn status', { error: err instanceof Error ? err.message : String(err) });
                    this.currentStep.set('error');
                    this.errorMessage.set('Could not connect to the server. Please try again later.');
                    this.isLoading.set(false);
                }
            });
        } catch (err) {
            this.log.error('Error during compatibility check', { error: err instanceof Error ? (err as Error).message : String(err) });
            this.currentStep.set('error');
            this.errorMessage.set('An unexpected error occurred during compatibility check.');
            this.isLoading.set(false);
        }
    }

    async startRegistration(): Promise<void> {
        if (!this.canRegister()) return;

        this.currentStep.set('registering');
        this.isLoading.set(true);
        this.errorMessage.set(null);

        try {
            // Get credential options from server
            const tokenValue = this.token();
            this.twoFactorService.getCredentialOptions(tokenValue || undefined).subscribe({
                next: async (options) => {
                    try {
                        // Create credential using browser API
                        const credential = await this.twoFactorService.createCredential(options);

                        if (!credential) {
                            throw new Error('No credential returned from authenticator');
                        }

                        // Convert to JSON for server
                        const credentialJSON = this.twoFactorService.credentialToJSON(credential);

                        // Register with server
                        const nameValue = this.deviceName().trim() || undefined;
                        this.twoFactorService.registerCredential(credentialJSON, tokenValue || undefined, nameValue).subscribe({
                            next: () => {
                                this.twoFactorService.markPasskeyRegistered();
                                this.currentStep.set('success');
                                this.isLoading.set(false);
                                this.log.info('WebAuthn credential registered successfully');
                            },
                            error: (err) => {
                                this.log.error('Error registering credential with server', { error: err instanceof Error ? err.message : String(err) });
                                this.currentStep.set('error');
                                this.errorMessage.set('Failed to register credential with server. Please try again.');
                                this.isLoading.set(false);
                            }
                        });
                    } catch (err: any) {
                        this.handleCredentialError(err);
                    }
                },
                error: (err) => {
                    this.log.error('Error getting credential options', { error: err instanceof Error ? err.message : String(err) });
                    this.currentStep.set('error');
                    this.errorMessage.set('Failed to get registration options from server.');
                    this.isLoading.set(false);
                }
            });
        } catch (err: any) {
            this.handleCredentialError(err);
        }
    }

    private handleCredentialError(err: any): void {
        this.isLoading.set(false);
        this.currentStep.set('error');

        if (err.name === 'NotAllowedError') {
            this.errorMessage.set('Registration was cancelled or timed out. Please try again.');
        } else if (err.name === 'InvalidStateError') {
            this.errorMessage.set('This authenticator is already registered.');
        } else if (err.name === 'NotSupportedError') {
            this.errorMessage.set('The authenticator does not support the required algorithms.');
        } else {
            this.errorMessage.set(err.message || 'An error occurred during registration.');
        }

        this.log.error('WebAuthn credential creation error', { error: err instanceof Error ? err.message : String(err), name: err.name });
    }

    complete(): void {
        this.registered.emit();
        this.close();
    }

    retry(): void {
        this.currentStep.set('check');
        this.errorMessage.set(null);
        this.checkCompatibility();
    }

    close(): void {
        this.visibleChange.emit(false);
    }
}
